// Third party
import React from 'react';
import { SortingRule } from 'react-table';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { LocationDescriptorObject } from 'history';

// Components
import FundSearchResult from './FundSearchResult';
import BenchmarkSearchResult from './BenchmarkSearchResult';
import { SearchResultColumns, SEARCH_COLUMN_MAPPING } from './columns-definition';
import { withErrorBoundary } from '@/views/components/errors';

// Selectors
import { selectAdvancedSearchShelfStatusedFund } from '@/redux/modules/shelf-management/selectors';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { Dispatch } from '@/redux';
import {
  searchAllFunds,
  searchAllBenchmarks,
  updateSearchSorting,
  setFocusedFund,
  setFocusedBenchmark,
  loadMoreFundResults,
  setSelectedBenchmarks,
  loadMoreBenchmarkResults,
} from '@/redux/modules/advanced-search';
import { toggleSavedFund } from '@/redux/modules/funds/saved-funds';
import { toggleSavedBenchmark } from '@/redux/modules/comparison-benchmarks/saved-benchmarks';
import { addFundsToComparison, addBenchmarksToComparison, ADD_FUNDS_TO_COMPARISON } from '@/redux/modules/comparison';
import { resetPortfolio, addFundsToPortfolio, ADD_FUNDS_TO_PORTFOLIO } from '@/redux/modules/edit-portfolio';
import { addSearchItemLog } from '@/redux/modules/activity-log';
import { addBenchmarksToPortfolio, loadNewBenchmark } from '@/redux/modules/edit-benchmark';
import { clearComplianceAlternatives } from '@/redux/modules/compliance-alternatives';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { SEARCH_ITEM_SOURCE, SEARCH_ITEM_DEST } from '@/utils/activity-log';
import { ADVANCED_SEARCH_OPTIONS, COMPARISON_ITEM_TYPES, PROXY_OPTION, FUND_SEARCH_TYPES, NAV_PATHS } from '@/utils/constants';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';
import { FundSearchState } from '@/redux/modules/types/fund-search';
import { BenchmarkSearchState } from '@/redux/modules/types/benchmark-search';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';
import SearchOptions from '@/types/search-options';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { MultiselectAction } from '@/types/data-table';
import AdvancedSearchItem from '@/types/advanced-search-item';

// Legacy Support
import withLegacyVersion from '../hocs/withLegacyVersion';
import LegacyAdvancedSearch from './LegacyAdvancedSearch';

type DispatchProps = {
  onSearch: (options: SearchOptions, sorting?: SortingRule[]) => void;
  updateSearchSorting: (sorting: SortingRule[], selectedOption: ADVANCED_SEARCH_OPTIONS) => void;
  toggleSavedFund: (fund: Fund) => Promise<Fund[]>;
  toggleSavedBenchmark: (benchmark: ComparisonBenchmark) => Promise<ComparisonBenchmark[]>;
  addFundsToComparison: (funds: Fund[]) => Promise<void>;
  resetPortfolio: () => void;
  setFocusedFund: (fund?: Fund) => void;
  addFundsToPortfolio: (funds: Fund[]) => Promise<void[]>;
  onLoadMoreResults: () => Promise<void>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  setSelectedBenchmarks: (benchmarks: ComparisonBenchmark[]) => void;
  setFocusedBenchmark: (benchmark?: ComparisonBenchmark) => void;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
  addBenchmarksToPortfolio: (benchmarks: ComparisonBenchmark[]) => Promise<void[]>;
  loadNewBenchmark: () => void;
  clearComplianceAlternatives: () => void;
};

type FunctionProps = {
  onRowClick?: (id: string) => void;
  onCompareAlternatives?: () => void;
};

type StateProps = {
  fund: FundSearchState;
  benchmark: BenchmarkSearchState;
  hasMore?: boolean;
  loadingResults: boolean;
  savedFunds: Fund[];
  savedBenchmarks: ComparisonBenchmark[];
  totalCount?: number;
  searchQuery: string;
  useWindowScrollAncestor?: boolean;
  user?: Nullable<User>;
  customTableActions?: JSX.Element[] | MultiselectAction<AdvancedSearchItem>[];
  addingToPortfolio: boolean;
  addingToComparison: boolean;
  tableColumns: string[];
  hideSaveFund: boolean;
};

type LegacyProps = {
  hideSaveFund?: boolean;
  isV2?: boolean;
  overrideShowArrow?: boolean;
  onArrowClick?: (fund: Fund) => void;
  setSelectedFunds?: (funds: Fund[]) => void;
};

type Props = DispatchProps &
  StateProps &
  FunctionProps &
  Partial<RouteComponentProps> & {
    classicVersionOnly?: boolean;
    insideModal?: boolean;
    instrumentType: ADVANCED_SEARCH_OPTIONS;
    hideCompareAllAlternatives?: boolean;
    compact?: boolean;
    canSelect?: boolean;
    proxyOption?: PROXY_OPTION;
    showInlineAdd?: boolean;
    onAddItem: (fund: Fund) => void;
  } & LegacyProps;

type State = {
  redirect?: string | LocationDescriptorObject;
};

type SharedResultProps = {
  classicVersionOnly?: boolean;
  insideModal?: boolean;
  loading: boolean;
  onLoadMoreResults: () => Promise<void>;
  hasMore?: boolean;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  columnPinning?: ColumnPinning;
  className?: string;
  showTicker?: boolean;
  columnOverrides?: Record<string, { size?: number; isRightAligned?: boolean }>;
  onSortingChange: (sort: SortingRule[]) => void;
  searchQuery: string;
  hideSaveFund: boolean;
  tableColumns: string[];
};

type ColumnPinning = {
  left: string[];
};

type ResultComponentConfig = {
  Component: React.ComponentType<any>;
  buildProps: (instance: AdvancedSearchResults, sharedProps: SharedResultProps) => Record<string, any>;
};

const COLUMN_OVERRIDES = {
  [SearchResultColumns.IS_SAVED]: { size: 16 },
  [SearchResultColumns.CLASSIFICATION]: { size: 185 },
  [SearchResultColumns.YT]: { isRightAligned: true },
  [SearchResultColumns.ONE_YEAR]: { isRightAligned: true },
  [SearchResultColumns.THREE_YEARS]: { isRightAligned: true },
  [SearchResultColumns.FIVE_YEARS]: { isRightAligned: true },
  [SearchResultColumns.TEN_YEARS]: { isRightAligned: true },
  [SearchResultColumns.INC]: { isRightAligned: true },
  [SearchResultColumns.MER]: { isRightAligned: true },
  [SearchResultColumns.BETA]: { isRightAligned: true },
  [SearchResultColumns.STD_DEV]: { isRightAligned: true },
};

const RESULT_COMPONENT_MAPPING: Partial<Record<ADVANCED_SEARCH_OPTIONS, ResultComponentConfig>> = {
  [ADVANCED_SEARCH_OPTIONS.FUNDS]: {
    Component: FundSearchResult,
    buildProps: (instance: AdvancedSearchResults, sharedProps: SharedResultProps) => {
      const {
        fund,
        savedFunds,
        compact,
        canSelect,
        customTableActions,
        showInlineAdd,
        onAddItem,
        onCompareAlternatives,
        addingToPortfolio,
        addingToComparison,
        user,
      } = instance.props;
      return {
        ...sharedProps,
        addingToPortfolio,
        addingToComparison,
        fundState: fund,
        onStartComparison: instance.onStartFundsComparison,
        onToggleSaved: instance.onToggleSavedFund,
        savedFunds,
        onAddToPortfolio: instance.onAddFundsToPortfolio,
        setFocusedFund: instance.props.setFocusedFund,
        compact,
        canSelect,
        hideCompareAllAlternatives: instance.props.hideCompareAllAlternatives,
        focusOnRowClick: instance.props.onRowClick === undefined,
        onRowClick: instance.props.onRowClick,
        customTableActions,
        showInlineAdd,
        onAddItem,
        onClearComplianceAlternatives: instance.props.clearComplianceAlternatives,
        onCompareAlternatives,
        overrideShowArrow: instance.props.overrideShowArrow,
        onArrowClick: instance.props.onArrowClick,
        user,
      };
    },
  },
  [ADVANCED_SEARCH_OPTIONS.PROXIES]: {
    Component: FundSearchResult,
    buildProps: (instance: AdvancedSearchResults, sharedProps: SharedResultProps) => {
      const fundsConfig = RESULT_COMPONENT_MAPPING[ADVANCED_SEARCH_OPTIONS.FUNDS];
      if (!fundsConfig) {
        return {};
      }
      return fundsConfig.buildProps(instance, sharedProps);
    },
  },
  [ADVANCED_SEARCH_OPTIONS.BENCHMARKS]: {
    Component: BenchmarkSearchResult,
    buildProps: (instance: AdvancedSearchResults, sharedProps: SharedResultProps) => {
      const { benchmark, savedBenchmarks, user } = instance.props;
      return {
        ...sharedProps,
        savedBenchmarks,
        benchmarkState: benchmark,
        setSelectedBenchmarks: instance.props.setSelectedBenchmarks,
        onToggleSaved: instance.onToggleSavedBenchmark,
        onStartComparison: instance.onStartBenchmarksComparison,
        onAddToPortfolio: instance.onAddBenchmarksToPortfolio,
      };
    },
  },
};

class AdvancedSearchResults extends React.Component<Props, State> {
  static defaultProps = {
    onAddItem: () => {},
  };

  state: State = {};

  componentDidMount() {
    this.props.onSearch({ query: '' });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.instrumentType !== this.props.instrumentType) {
      this.props.onSearch({ query: '' });
    }
  }

  onSortingChange = (sort: SortingRule[]) => {
    this.props.updateSearchSorting(sort, this.props.instrumentType);
  };

  onToggleSavedFund = (row: Fund) => {
    const {
      fund: { funds },
    } = this.props;
    if (!funds) {
      return;
    }

    const fund = funds.find(f => f._id === row._id);
    if (!fund) {
      return;
    }

    this.props.toggleSavedFund(fund);
  };

  onToggleSavedBenchmark = (row: ComparisonBenchmark) => {
    const {
      benchmark: { benchmarks },
    } = this.props;
    if (!benchmarks) {
      return;
    }

    const benchmark = benchmarks.find(f => f._id === row._id);
    if (!benchmark) {
      return;
    }

    this.props.toggleSavedBenchmark(benchmark);
  };

  onStartFundsComparison = (selectedFunds: Fund[]) => {
    const { location } = this.props;

    this.props.addFundsToComparison(selectedFunds).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  onAddFundsToPortfolio = (selectedFunds: Fund[]) => {
    this.props.resetPortfolio();
    this.props.addFundsToPortfolio(selectedFunds).then(() => {
      for (const fund of selectedFunds) {
        this.props.addSearchItemLog(fund._id, COMPARISON_ITEM_TYPES.FUND, SEARCH_ITEM_SOURCE.ADVANCED_SEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
      }

      this.setState({ redirect: '/models?newPortfolio=true' });
    });
  };

  onStartBenchmarksComparison = () => {
    const {
      benchmark: { selectedBenchmarks },
      location,
    } = this.props;

    this.props.addBenchmarksToComparison(selectedBenchmarks).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  onAddBenchmarksToPortfolio = () => {
    const {
      benchmark: { selectedBenchmarks },
    } = this.props;

    this.props.loadNewBenchmark();

    this.props.addBenchmarksToPortfolio(selectedBenchmarks).then(() => {
      this.setState({ redirect: '/models/custom-benchmarks?newBenchmarkPortfolio=true' });
    });
  };

  renderSearchResults = () => {
    const {
      insideModal,
      loadingResults,
      totalCount,
      hasMore,
      useWindowScrollAncestor,
      instrumentType,
      classicVersionOnly,
      onLoadMoreResults,
      searchQuery,
      hideSaveFund,
      tableColumns,
    } = this.props;

    const sharedProps: SharedResultProps = {
      classicVersionOnly,
      insideModal,
      loading: loadingResults,
      onLoadMoreResults,
      hasMore,
      totalResultsCount: totalCount,
      useWindowScrollAncestor,
      columnPinning: {
        left: ['select', SearchResultColumns.IS_SAVED, SearchResultColumns.TICKER, SearchResultColumns.NAME],
      },
      className: 'advanced-search-results',
      showTicker: true,
      columnOverrides: COLUMN_OVERRIDES,
      onSortingChange: this.onSortingChange,
      searchQuery,
      hideSaveFund,
      tableColumns,
    };

    const config = RESULT_COMPONENT_MAPPING[instrumentType as keyof typeof RESULT_COMPONENT_MAPPING];
    if (!config) {
      return null;
    }

    const { Component, buildProps } = config;
    const componentProps = buildProps(this, sharedProps);

    return <Component {...componentProps} />;
  };

  render() {
    const { insideModal } = this.props;
    const { redirect } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const classes = classNames('advanced-search d-flex flex-column', {
      'inside-modal': insideModal,
    });

    return <div className={classes}>{this.renderSearchResults()}</div>;
  }
}

const mapStateToProps = (state: ReduxState, ownProps: { instrumentType: ADVANCED_SEARCH_OPTIONS }) => ({
  user: state.auth.user,
  loadingResults: state.advancedSearch.loadingResults,
  fund: selectAdvancedSearchShelfStatusedFund(state.advancedSearch),
  benchmark: state.advancedSearch.benchmark,
  savedFunds: state.funds.savedFunds.funds,
  savedBenchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  totalCount: state.advancedSearch.totalCount,
  searchQuery: state.advancedSearch.searchOptions?.query,
  hasMore: state.advancedSearch.hasMore,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  addingToComparison: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
  tableColumns: SEARCH_COLUMN_MAPPING[ownProps.instrumentType] || [],
  hideSaveFund: !userHasPermission(state.auth.user, PERMISSIONS.SECTION_SEARCH),
});

const mapDispatchToProps = (dispatch: Dispatch, ownProps: { instrumentType: ADVANCED_SEARCH_OPTIONS; proxyOption?: PROXY_OPTION }) => {
  const LOAD_MORE_ACTION_MAP: Partial<Record<ADVANCED_SEARCH_OPTIONS, typeof loadMoreFundResults>> = {
    [ADVANCED_SEARCH_OPTIONS.BENCHMARKS]: loadMoreBenchmarkResults,
    [ADVANCED_SEARCH_OPTIONS.FUNDS]: loadMoreFundResults,
    [ADVANCED_SEARCH_OPTIONS.PROXIES]: loadMoreFundResults,
  };

  const searchProxies = (options: SearchOptions, sorting?: SortingRule[]) => {
    if (!ownProps.proxyOption) {
      return;
    }
    const searchOptions = {
      ...options,
      types: [FUND_SEARCH_TYPES.BENCHMARK_PROXY],
    };
    return dispatch(searchAllFunds(searchOptions, sorting));
  };

  const SEARCH_ACTION_MAP: Partial<Record<ADVANCED_SEARCH_OPTIONS, (options: SearchOptions, sorting?: SortingRule[]) => void>> = {
    [ADVANCED_SEARCH_OPTIONS.BENCHMARKS]: (options: SearchOptions, sorting?: SortingRule[]) => dispatch(searchAllBenchmarks(options, sorting)),
    [ADVANCED_SEARCH_OPTIONS.FUNDS]: (options: SearchOptions, sorting?: SortingRule[]) => dispatch(searchAllFunds(options, sorting)),
    [ADVANCED_SEARCH_OPTIONS.PROXIES]: searchProxies,
  };

  const loadMoreAction = LOAD_MORE_ACTION_MAP[ownProps.instrumentType] || loadMoreFundResults;
  const searchAction =
    SEARCH_ACTION_MAP[ownProps.instrumentType] || ((options: SearchOptions, sorting?: SortingRule[]) => dispatch(searchAllFunds(options, sorting)));

  return {
    ...bindActionCreators(
      {
        updateSearchSorting,
        toggleSavedFund,
        addFundsToComparison,
        resetPortfolio,
        setFocusedFund,
        addFundsToPortfolio,
        addSearchItemLog,
        setSelectedBenchmarks,
        setFocusedBenchmark,
        toggleSavedBenchmark,
        addBenchmarksToComparison,
        addBenchmarksToPortfolio,
        loadNewBenchmark,
        clearComplianceAlternatives,
      },
      dispatch,
    ),
    onLoadMoreResults: () => dispatch(loadMoreAction()),
    onSearch: (options: SearchOptions, sorting?: SortingRule[]) => searchAction(options, sorting),
  };
};

export default withLegacyVersion(connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(AdvancedSearchResults)), LegacyAdvancedSearch);
